import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Area,
  ComposedChart,
} from 'recharts';
import { Loader2 } from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}`;
};

// Module-level constants for chart props (stable references)
const CHART_MARGIN = { top: 10, right: 20, left: 0, bottom: 5 };
const TOOLTIP_STYLE = { borderRadius: 8, fontSize: 13 };
const TOOLTIP_FORMATTER = (value) => [`${value} pts`, 'Score'];
const TOOLTIP_LABEL_FORMATTER = (l, payload) => {
  const fullDate = payload?.[0]?.payload?.date;
  return fullDate || l;
};
const REFLINE_CRITICAL_LABEL = { value: 'Crítico', fill: '#dc2626', fontSize: 11, position: 'right' };
const REFLINE_HIGH_LABEL = { value: 'Alto', fill: '#ea580c', fontSize: 11, position: 'right' };

function PriorityHistoryModal({ projectId, projectName, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await api.get(`/priority/history/${projectId}`, { params: { days } });
      setData(r.data);
    } catch (err) {
      console.error('history load error', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, days]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const chartData = (data?.points || []).map((p) => ({
    date: p.date,
    label: formatDate(p.date),
    score: p.score,
  }));

  const trend =
    chartData.length >= 2
      ? chartData[chartData.length - 1].score - chartData[0].score
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="priority-history-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Evolução do Score — <span className="text-emerald-700">{projectName}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-600">Janela:</span>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-sm rounded-full border transition ${
                days === d
                  ? 'bg-emerald-700 text-white border-emerald-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid={`priority-history-range-${d}`}
            >
              {d} dias
            </button>
          ))}
          {chartData.length >= 2 && (
            <span
              className={`ml-auto text-sm font-medium ${
                trend > 0 ? 'text-red-600' : trend < 0 ? 'text-emerald-700' : 'text-gray-500'
              }`}
            >
              {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend)} pts no período
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-700" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-gray-500">
            <p className="text-sm">Sem histórico ainda para este projeto.</p>
            <p className="text-xs mt-1">A primeira execução do agendador (07h BRT) começará a registrar.</p>
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={CHART_MARGIN}>
                <defs>
                  <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#15803d" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#15803d" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
                <YAxis domain={[0, 100]} stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={TOOLTIP_FORMATTER}
                  labelFormatter={TOOLTIP_LABEL_FORMATTER}
                />
                <ReferenceLine y={80} stroke="#dc2626" strokeDasharray="4 4" label={REFLINE_CRITICAL_LABEL} />
                <ReferenceLine y={60} stroke="#ea580c" strokeDasharray="4 4" label={REFLINE_HIGH_LABEL} />
                <Area type="monotone" dataKey="score" stroke="none" fill="url(#scoreGrad)" />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#15803d"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#15803d' }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center pt-2">
          Score atual: <strong>{data?.current_score ?? '—'}</strong> · Pontos no período:{' '}
          <strong>{chartData.length}</strong> · Histórico atualizado diariamente às 07:00 BRT
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default PriorityHistoryModal;
